import AttachmentApi from "../api/services/AttachmentApi";
import ItemCache from "./ItemCache";
import LoadedFile from "../components/Gallery/model/LoadedFile.model";
import AttachmentModel from "../api/models/Attachment";
import FileReference from "../components/Gallery/model/FileReference.model";
import Attachment from "../components/Gallery/model/Attachment.model";
import GalleryFlag from "../components/Gallery/model/GalleryFlag.model";
import { GeneralFileType } from "../components/Gallery/GalleryFilter";
import FileHandler from "../components/Gallery/model/FileHandler.model";
import AsyncCache from "./AsyncCache";
import SemiParallelSequence from "./SemiParallelSequence";

export interface RemoveAttachmentResult {
  idToBeDeleted: string[];
  attachments: Attachment[];
}

export interface AppendService {
  append(ids: string[], flag?: GalleryFlag): Promise<Attachment[]>;

  remove(ids: string[]): Promise<RemoveAttachmentResult>;

  zipFileName(files: FileReference[], generalFilterType: keyof typeof GeneralFileType): string;

  fileName?(file: LoadedFile): string;
}

export default class AttachmentFileHandler implements FileHandler {
  private thumbnailCache: ItemCache<string> = new ItemCache<string>("thumbnail", 24 * 60 * 60 * 1000, 20);

  private galleryCache: ItemCache<string> = new ItemCache<string>("gallery", 24 * 60 * 60 * 1000, 8);

  private accessTokenCache: AsyncCache<string>;

  private sequence: SemiParallelSequence<string> = new SemiParallelSequence<string>(5);

  constructor(
    private attachmentApi: AttachmentApi,
    private appendService: AppendService,
    getAccessToken: () => Promise<string>,
  ) {
    this.accessTokenCache = new AsyncCache<string>(getAccessToken);
  }

  private static fileExtension(fileName: string): string {
    const parts = fileName.split(".");
    if (parts.length === 1) {
      return "STH";
    }
    return parts[parts.length - 1].substr(0, 4);
  }

  public static toLoadedFile(
    attachment: AttachmentModel,
    attachmentEditable: TestRequestRaw.Schemas.AttachmentEditable,
    flag?: GalleryFlag,
  ): LoadedFile {
    return {
      id: attachment.uuid,
      isImage: !attachment.thumbnailsDisabled,
      description: attachment.description,
      editable: attachmentEditable,
      status: "LOADED",
      type: AttachmentFileHandler.fileExtension(attachment.name),
      name: attachment.name,
      flag,
      tags: attachment.tags,
    };
  }

  delete(ids: string[]): Promise<Attachment[]> {
    this.accessTokenCache.reset();
    const reduce = ids
      .map(
        (id) => () =>
          this.appendService.remove([id]).then((removeAttachmentResult) => {
            return removeAttachmentResult.attachments;
          }),
      )
      .reduce(
        (previousValue, currentValue) => () => previousValue().then(() => currentValue()),
        () => Promise.resolve([]),
      );
    return reduce();
  }

  downloadThumbnail(id: string): Promise<string> {
    const item = this.thumbnailCache.getItem(id);
    if (item) {
      return Promise.resolve(item);
    }
    return this.sequence.fetch(() =>
      this.accessTokenCache
        .fetch()
        .then((token) =>
          this.attachmentApi.downloadThumbnail({
            accessToken: token,
            attachmentIds: [id],
          }),
        )
        .then((image) => this.thumbnailCache.storeItem(id, image)),
    );
  }

  downloadGallery(id: string): Promise<string> {
    const item = this.galleryCache.getItem(id);
    if (item) {
      return Promise.resolve(item);
    }
    return this.accessTokenCache.fetch().then((token) =>
      this.attachmentApi
        .downloadGallery({
          accessToken: token,
          attachmentIds: [id],
        })
        .then((image) => this.galleryCache.storeItem(id, image)),
    );
  }

  downloadZip(files: FileReference[], generalFileType: keyof typeof GeneralFileType): Promise<void> {
    const zipFileName = this.appendService.zipFileName(files, generalFileType);
    return this.accessTokenCache.fetch().then((token) =>
      this.attachmentApi.handleDownload(zipFileName, {
        accessToken: token,
        attachmentIds: files.map((file) => file.id),
      }),
    );
  }

  downloadFile(file: LoadedFile): Promise<void> {
    const fileName = this.appendService.fileName?.(file);
    return this.accessTokenCache.fetch().then((token) =>
      this.attachmentApi.handleDownload(fileName || file.name, {
        accessToken: token,
        attachmentIds: [file.id],
      }),
    );
  }

  updateDescription(file: LoadedFile, newDescription: string): Promise<LoadedFile> {
    return this.accessTokenCache
      .fetch()
      .then((token) =>
        this.attachmentApi
          .updateAttachmentDescription(file.id, newDescription, token)
          .then((response) => AttachmentFileHandler.toLoadedFile(response.data, file.editable, file.flag)),
      );
  }

  load(attachments: Attachment[]): Promise<LoadedFile[]> {
    return this.accessTokenCache.fetch().then((token) =>
      this.attachmentApi
        .fetchAttachments({
          accessToken: token,
          attachmentIds: attachments.map((a) => a.uuid),
        })
        .then((attachmentModels) => {
          return attachments
            .map((a) => {
              const attachmentModel = attachmentModels.find((am) => am.uuid === a.uuid);
              if (attachmentModel) {
                return AttachmentFileHandler.toLoadedFile(attachmentModel, a.editable, a.flag);
              }
              return null;
            })
            .filter((x) => !!x)
            .map((x) => x as LoadedFile);
        }),
    );
  }

  attach(file: LoadedFile, flag?: GalleryFlag): Promise<Attachment[]> {
    this.accessTokenCache.reset();
    return this.appendService.append([file.id], flag);
  }
}
